import React from 'react';
import clsx from 'clsx';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Tooltip } from 'recharts';
import { getDatabase, onValue, ref, runTransaction, serverTimestamp, set } from 'firebase/database';
import { Button, Card, CardBody, CardHeader, Input } from '../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { firebaseApp } from '../firebase';
import { isCommitteeAdmin } from '../lib/admin';
import { getConferenceSession } from '../lib/firestore';
import { isPresenterSupport, isSessionPresenter } from '../lib/sessionPresenters';
import type { Session } from '../lib/types';

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#a855f7', '#06b6d4', '#f97316', '#10b981'];

const LS_VOTER_ID = 'slugnet_poll_voter_id';

type PollQuestionType = 'single-choice' | 'multi-choice';
type DraftQuestion = { question: string; options: string[]; type?: PollQuestionType; maxChoices?: number };

const rtdb = getDatabase(firebaseApp);

function makeId() {
  const rand = Math.random().toString(36).slice(2, 10);
  const time = Date.now().toString(36);
  return `${time}-${rand}`;
}

function pollRef(pollId: string) {
  return ref(rtdb, `polls/${pollId}`);
}

function getVoterId() {
  try {
    let id = localStorage.getItem(LS_VOTER_ID);
    if (!id) {
      id = (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)) + '-' + Date.now().toString(36);
      localStorage.setItem(LS_VOTER_ID, id);
    }
    return id;
  } catch {
    return 'anon-' + Math.random().toString(36).slice(2);
  }
}

function voteStorageKey(pollId: string, qIdx: number) {
  return `slugnet_poll_vote_${pollId}_${qIdx}`;
}

function readLocalVote(pollId: string, qIdx: number) {
  try {
    const raw = localStorage.getItem(voteStorageKey(pollId, qIdx));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeLocalVote(pollId: string, qIdx: number, data: any) {
  try {
    localStorage.setItem(voteStorageKey(pollId, qIdx), JSON.stringify(data));
  } catch {
    // ignore
  }
}

function clearLocalVote(pollId: string, qIdx: number) {
  try {
    localStorage.removeItem(voteStorageKey(pollId, qIdx));
  } catch {
    // ignore
  }
}

function questionType(question: any): PollQuestionType {
  if (Number(question?.maxChoices || 0) > 1) return 'multi-choice';
  return question?.type === 'multi-choice' ? 'multi-choice' : 'single-choice';
}

function questionMaxChoices(question: any) {
  const optionCount = Array.isArray(question?.options) ? question.options.length : 0;
  const max = Number(question?.maxChoices || 0);
  if (!Number.isFinite(max) || max < 1) return optionCount || 1;
  return Math.max(1, Math.min(optionCount || max, Math.floor(max)));
}

function uniqueValidChoices(value: number | number[], optionCount: number, maxChoices: number) {
  const values = (Array.isArray(value) ? value : [value])
    .map((item) => Number(item))
    .filter((item) => Number.isInteger(item) && item >= 0 && item < optionCount);
  return Array.from(new Set(values)).slice(0, maxChoices);
}

function choiceIndexesFromStoredVote(value: any, question: any) {
  const optionCount = Array.isArray(question?.options) ? question.options.length : 0;
  const maxChoices = questionType(question) === 'multi-choice' ? questionMaxChoices(question) : 1;
  if (Array.isArray(value?.choiceIdxs)) return uniqueValidChoices(value.choiceIdxs, optionCount, maxChoices);
  if (typeof value?.choiceIdx === 'number') return uniqueValidChoices(value.choiceIdx, optionCount, 1);
  return [];
}

function sameChoices(a: number[], b: number[]) {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function storedVotePayload(question: any, choices: number[], resetCounter: number) {
  return questionType(question) === 'multi-choice'
    ? { choiceIdxs: choices, resetCounter }
    : { choiceIdx: choices[0], resetCounter };
}

function OrderedLegend(props: {
  data: Array<{ name: string; value: number }>;
  compact?: boolean;
  legacy?: boolean;
}) {
  if (!props.data.length) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: props.compact ? 'row' : 'column',
        flexWrap: props.compact ? 'wrap' : 'nowrap',
        justifyContent: props.compact ? 'center' : 'flex-start',
        gap: props.compact ? '6px 12px' : 8,
        maxWidth: '100%',
        minWidth: 0
      }}
    >
      {props.data.map((item, idx) => (
        <div
          key={`${item.name}-${idx}`}
          style={{
            display: 'inline-flex',
            alignItems: 'flex-start',
            gap: 6,
            fontSize: 12,
            color: props.legacy ? undefined : '#3f3f46',
            maxWidth: '100%',
            minWidth: 0
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              backgroundColor: COLORS[idx % COLORS.length],
              flex: '0 0 auto',
              marginTop: 3
            }}
          />
          <span style={{ minWidth: 0, whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.25 }}>
            {item.name} <span className={props.legacy ? 'legend-count' : 'text-zinc-500'}>({item.value})</span>
          </span>
        </div>
      ))}
    </div>
  );
}

function pollAvailableFromMs(poll: any) {
  const value = poll?.availableFrom;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function pollAvailableUntilMs(poll: any) {
  const value = poll?.availableUntil;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function formatPollAvailableFrom(value: number) {
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

function useWindowSize() {
  const [size, setSize] = React.useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1200,
    h: typeof window !== 'undefined' ? window.innerHeight : 800
  }));

  React.useEffect(() => {
    const onResize = () => setSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return size;
}

function useBodyClass(className: string) {
  React.useEffect(() => {
    document.body.classList.add(className);
    return () => document.body.classList.remove(className);
  }, [className]);
}

function usePoll(pollId: string | null) {
  const [poll, setPoll] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<any>(null);

  React.useEffect(() => {
    if (!pollId) return;
    setLoading(true);
    setError(null);
    const unsub = onValue(
      pollRef(pollId),
      (snap) => {
        setPoll(snap.val());
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [pollId]);

  return { poll, loading, error };
}

async function createPoll({ questions }: { questions: DraftQuestion[] }) {
  const pollId = makeId();
  const payload = {
    id: pollId,
    currentIdx: 0,
    questions: questions.map((q) => ({
      question: q.question,
      type: q.type ?? 'single-choice',
      ...(q.type === 'multi-choice' ? { maxChoices: q.maxChoices ?? q.options.length } : {}),
      options: q.options.map((label) => ({ label, votes: 0 })),
      resetCounter: 0,
      votesByVoter: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  await set(pollRef(pollId), payload);
  return pollId;
}

async function vote(pollId: string, qIdx: number, choice: number | number[], voterId: string) {
  const pollRef = ref(rtdb, `polls/${pollId}`);
  await runTransaction(pollRef, (current) => {
    if (!current) return current;
    const availableFrom = pollAvailableFromMs(current);
    if (availableFrom != null && availableFrom > Date.now()) return current;
    const availableUntil = pollAvailableUntilMs(current);
    if (availableUntil != null && availableUntil <= Date.now()) return current;

    const next = { ...current };
    const questions = Array.isArray(next.questions) ? next.questions.map((q: any) => ({ ...q })) : [];
    if (!questions[qIdx]) return current;

    const q = { ...questions[qIdx] };
    const resetCounter = q.resetCounter || 0;

    const opts = Array.isArray(q.options) ? q.options.map((o: any) => ({ ...o })) : [];
    q.options = opts;
    const maxChoices = questionType(q) === 'multi-choice' ? questionMaxChoices(q) : 1;
    const nextChoices = uniqueValidChoices(choice, opts.length, maxChoices).sort((a, b) => a - b);
    if (!nextChoices.length) return current;

    const vbv = q.votesByVoter && typeof q.votesByVoter === 'object' ? { ...q.votesByVoter } : {};
    const prev = vbv[voterId];
    const prevValid = prev && prev.resetCounter === resetCounter;
    const prevChoices = prevValid ? choiceIndexesFromStoredVote(prev, q).sort((a, b) => a - b) : [];

    if (prevChoices.length) {
      if (sameChoices(prevChoices, nextChoices)) return current;
      prevChoices.forEach((pi) => {
        if (!opts[pi]) return;
        const pv = typeof opts[pi].votes === 'number' ? opts[pi].votes : 0;
        opts[pi].votes = Math.max(0, pv - 1);
      });
    }

    nextChoices.forEach((optionIndex) => {
      if (!opts[optionIndex]) return;
      const cv = typeof opts[optionIndex].votes === 'number' ? opts[optionIndex].votes : 0;
      opts[optionIndex].votes = cv + 1;
    });

    vbv[voterId] =
      questionType(q) === 'multi-choice'
        ? { choiceIdxs: nextChoices, resetCounter, at: Date.now() }
        : { choiceIdx: nextChoices[0], resetCounter, at: Date.now() };
    q.votesByVoter = vbv;
    q.updatedAt = Date.now();

    questions[qIdx] = q;
    next.questions = questions;
    next.updatedAt = Date.now();
    return next;
  });
}

function PollResultsCard(props: {
  question: string;
  options: Array<{ label: string; votes?: number }>;
  height: number;
  toolbar?: React.ReactNode;
}) {
  const data = React.useMemo(
    () => (props.options?.length ? props.options.map((o) => ({ name: o.label, value: o.votes || 0 })) : []),
    [props.options]
  );
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  const isCompact = props.height <= 360;

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-zinc-300 bg-zinc-200 px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-zinc-900">{props.question}</div>
          <div className="mt-1 text-xs text-zinc-600">Total votes: {total}</div>
        </div>
        {props.toolbar ? <div className="flex flex-wrap items-center justify-end gap-2">{props.toolbar}</div> : null}
      </div>
      <CardBody>
        <div
          style={{
            width: '100%',
            height: props.height,
            display: 'grid',
            gridTemplateColumns: isCompact ? '1fr' : 'minmax(0, 1fr) minmax(240px, 340px)',
            gridTemplateRows: isCompact ? 'minmax(0, 1fr) auto' : '1fr',
            alignItems: 'center',
            gap: isCompact ? 12 : 18,
            minWidth: 0,
          }}
        >
          <div style={{ width: '100%', height: '100%', minWidth: 0, minHeight: 0 }}>
            <ResponsiveContainer>
              <PieChart>
                <Tooltip />
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={isCompact ? Math.max(40, Math.min(85, props.height * 0.18)) : Math.max(55, Math.min(130, props.height * 0.20))}
                  outerRadius={isCompact ? Math.max(80, Math.min(145, props.height * 0.30)) : Math.max(105, Math.min(220, props.height * 0.34))}
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {data.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ minWidth: 0, maxHeight: '100%', overflow: 'auto' }}>
            <OrderedLegend data={data} compact={isCompact} />
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function LegacyCard(props: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  return (
    <div className={['card', props.className].filter(Boolean).join(' ')} style={props.style}>
      {props.children}
    </div>
  );
}

function LegacyPieResults(props: {
  question: string;
  options: Array<{ label: string; votes?: number }>;
  height?: number;
  toolbar?: React.ReactNode;
}) {
  const data = React.useMemo(
    () => (props.options?.length ? props.options.map((o) => ({ name: o.label, value: o.votes || 0 })) : []),
    [props.options]
  );
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  const height = props.height ?? 360;
  const isCompact = height <= 360;

  return (
    <LegacyCard className="p-16">
      <div className="hstack between gap-12 mb-12">
        <div>
          <div style={{ fontSize: 20, fontWeight: 750, marginTop: 4, lineHeight: 1.25 }}>{props.question}</div>
          <div className="small mt-8">Total votes: {total}</div>
        </div>
        {props.toolbar ? <div className="hstack gap-8 toolbar-wrap">{props.toolbar}</div> : null}
      </div>

      <div style={{ width: '100%', height, overflow: 'hidden' }}>
        <ResponsiveContainer>
          <PieChart>
            <Tooltip />
            <Legend
              verticalAlign={isCompact ? 'bottom' : 'middle'}
              align={isCompact ? 'center' : 'right'}
              layout={isCompact ? 'horizontal' : 'vertical'}
              content={() => <OrderedLegend data={data} compact={isCompact} legacy />}
            />
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx={isCompact ? '50%' : '36%'}
              cy="50%"
              innerRadius={isCompact ? Math.max(40, Math.min(85, height * 0.18)) : Math.max(55, Math.min(130, height * 0.22))}
              outerRadius={isCompact ? Math.max(80, Math.min(145, height * 0.30)) : Math.max(110, Math.min(240, height * 0.42))}
              paddingAngle={2}
              isAnimationActive={false}
            >
              {data.map((_, idx) => (
                <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    </LegacyCard>
  );
}

function LegacyResultsList(props: {
  question: string;
  options: Array<{ label: string; votes?: number }>;
}) {
  const data = React.useMemo(
    () => (props.options?.length ? props.options.map((o) => ({ name: o.label, value: o.votes || 0 })) : []),
    [props.options]
  );
  const total = data.reduce((s, d) => s + (d.value || 0), 0);

  return (
    <LegacyCard className="p-16">
      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{props.question}</div>
      <div className="small mt-8">Total votes: {total}</div>

      <div className="poll-results-list mt-16">
        {data.map((item, idx) => {
          const percent = total > 0 ? Math.round((item.value / total) * 100) : 0;
          return (
            <div key={`${item.name}-${idx}`} className="poll-result-row">
              <div className="poll-result-row-main">
                <span
                  aria-hidden="true"
                  className="poll-result-swatch"
                  style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                />
                <span className="poll-result-label">{item.name}</span>
                <span className="poll-result-count">{item.value}</span>
              </div>
              <div className="poll-result-bar" aria-hidden="true">
                <div
                  className="poll-result-bar-fill"
                  style={{
                    width: `${percent}%`,
                    backgroundColor: COLORS[idx % COLORS.length],
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </LegacyCard>
  );
}

export default function PollPage() {
  const { pollId: routePollId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = React.useState<'create' | 'live'>(() => (routePollId ? 'live' : 'create'));
  const [question, setQuestion] = React.useState('');
  const [answers, setAnswers] = React.useState(['', '']);
  const [questionsQueue, setQuestionsQueue] = React.useState<DraftQuestion[]>([]);
  const [pollId, setPollId] = React.useState<string | null>(() => routePollId ?? null);
  const [showQr, setShowQr] = React.useState(true);
  const [creating, setCreating] = React.useState(false);
  const [hostSession, setHostSession] = React.useState<Session | null>(null);
  const [hostAccessLoading, setHostAccessLoading] = React.useState(false);
  const [hostAccessChecked, setHostAccessChecked] = React.useState(() => !routePollId);

  const { poll, loading: pollLoading, error: pollError } = usePoll(pollId);
  const { h: vh } = useWindowSize();

  const chartHeight = React.useMemo(() => {
    const base = Math.max(520, vh - 220);
    return Math.min(860, base);
  }, [vh]);

  const qrSize = React.useMemo(() => {
    const target = Math.floor(chartHeight * 0.8);
    return Math.max(260, Math.min(520, target));
  }, [chartHeight]);

  const shareUrl = React.useMemo(() => {
    if (!pollId || typeof window === 'undefined') return null;
    return `${window.location.origin}/poll/p/${pollId}`;
  }, [pollId]);

  const canCreate =
    question.trim().length > 0 && answers.filter((a) => a.trim().length > 0).length >= 2 && !creating;

  async function onCreate() {
    if (creating) return;

    const draftValid = question.trim().length > 0 && answers.filter((a) => a.trim().length > 0).length >= 2;
    const draft = draftValid
      ? { question: question.trim(), options: answers.map((a) => a.trim()).filter(Boolean) }
      : null;

    const questions = [...questionsQueue, ...(draft ? [draft] : [])];
    if (questions.length < 1) return;

    setCreating(true);
    try {
      const id = await createPoll({ questions });
      setPollId(id);
      setStep('live');
    } finally {
      setCreating(false);
    }
  }

  function addToQueue() {
    if (!canCreate) return;
    const draft = { question: question.trim(), options: answers.map((a) => a.trim()).filter(Boolean) };
    setQuestionsQueue((prev) => [...prev, draft]);
    setQuestion('');
    setAnswers(['', '']);
  }

  function removeQueued(idx: number) {
    setQuestionsQueue((prev) => prev.filter((_, i) => i !== idx));
  }

  function moveQueued(idx: number, dir: number) {
    setQuestionsQueue((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      const tmp = next[idx];
      next[idx] = next[j];
      next[j] = tmp;
      return next;
    });
  }

  function updateAnswer(i: number, v: string) {
    setAnswers((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
  }

  function addAnswer() {
    setAnswers((prev) => [...prev, '']);
  }

  function removeAnswer(i: number) {
    setAnswers((prev) => prev.filter((_, idx) => idx !== i));
  }

  function reset() {
    setStep('create');
    setPollId(null);
    setQuestion('');
    setAnswers(['', '']);
    setQuestionsQueue([]);
  }

  async function resetVotes() {
    if (!pollId) return;
    const pollRef = ref(rtdb, `polls/${pollId}`);
    await runTransaction(pollRef, (current) => {
      if (!current) return current;
      const next = { ...current };
      const currentIdx = typeof next.currentIdx === 'number' ? next.currentIdx : 0;
      const questions = Array.isArray(next.questions) ? next.questions.map((q: any) => ({ ...q })) : [];
      if (!questions[currentIdx]) return current;

      const q = { ...questions[currentIdx] };
      const opts = Array.isArray(q.options) ? q.options.map((o: any) => ({ ...o, votes: 0 })) : [];
      q.options = opts;
      q.resetCounter = (q.resetCounter || 0) + 1;
      q.votesByVoter = null;
      q.updatedAt = Date.now();

      questions[currentIdx] = q;
      next.questions = questions;
      next.updatedAt = Date.now();
      return next;
    });
  }

  async function setCurrentIdx(nextIdx: number) {
    if (!pollId) return;
    const pollRef = ref(rtdb, `polls/${pollId}`);
    await runTransaction(pollRef, (current) => {
      if (!current) return current;
      const max = Array.isArray(current.questions) ? current.questions.length - 1 : 0;
      const clamped = Math.max(0, Math.min(max, nextIdx));
      if ((current.currentIdx ?? 0) === clamped) return current;
      return { ...current, currentIdx: clamped, updatedAt: Date.now() };
    });
  }

  const currentIdx = typeof poll?.currentIdx === 'number' ? poll.currentIdx : 0;
  const totalQuestions = Array.isArray(poll?.questions) ? poll.questions.length : 0;
  const activeQuestion = totalQuestions ? poll.questions[currentIdx] : null;

  const goPrev = () => setCurrentIdx(currentIdx - 1);
  const goNext = () => setCurrentIdx(currentIdx + 1);
  function goBack() {
    if (window.history.length > 1) navigate(-1);
    else if (hostSession?.id) navigate(`/conference/${encodeURIComponent(hostSession.id)}`);
    else navigate('/conference');
  }

  const existingHostRoute = Boolean(routePollId);
  const canHostExistingPoll = Boolean(
    !existingHostRoute ||
      (hostSession &&
        (isCommitteeAdmin(user) ||
          isPresenterSupport(user, hostSession.conferenceId) ||
          isSessionPresenter(hostSession, user)))
  );

  const showQrPanel = Boolean(showQr && shareUrl);
  const liveGridClass = clsx('grid gap-4', showQrPanel ? 'lg:grid-cols-[minmax(0,1fr)_360px]' : 'grid-cols-1');

  React.useEffect(() => {
    if (!routePollId) return;
    setPollId(routePollId);
    setStep('live');
  }, [routePollId]);

  React.useEffect(() => {
    if (!routePollId) {
      setHostSession(null);
      setHostAccessLoading(false);
      setHostAccessChecked(true);
      return;
    }

    if (!poll) {
      setHostSession(null);
      setHostAccessLoading(false);
      setHostAccessChecked(false);
      return;
    }

    if (!poll?.conferenceId || !poll?.sessionId) {
      setHostSession(null);
      setHostAccessLoading(false);
      setHostAccessChecked(true);
      return;
    }

    let active = true;
    setHostAccessLoading(true);
    setHostAccessChecked(false);
    getConferenceSession(String(poll.conferenceId), String(poll.sessionId))
      .then((session) => {
        if (active) setHostSession(session);
      })
      .catch(() => {
        if (active) setHostSession(null);
      })
      .finally(() => {
        if (active) {
          setHostAccessLoading(false);
          setHostAccessChecked(true);
        }
      });

    return () => {
      active = false;
    };
  }, [routePollId, poll?.conferenceId, poll?.sessionId]);

  if (existingHostRoute) {
    if (pollLoading || hostAccessLoading || !hostAccessChecked) {
      return (
        <Card>
          <CardHeader
            title="Poll host"
            right={
              <Button variant="secondary" onClick={goBack}>
                Back
              </Button>
            }
          />
          <CardBody>
            <div className="text-sm text-zinc-600">Checking poll host access...</div>
          </CardBody>
        </Card>
      );
    }

    if (pollError || !poll) {
      return (
        <Card>
          <CardHeader
            title="Poll host"
            right={
              <Button variant="secondary" onClick={goBack}>
                Back
              </Button>
            }
          />
          <CardBody className="space-y-1">
            <div className="text-sm font-semibold text-zinc-900">Poll not found</div>
            <div className="text-sm text-zinc-600">The poll link may be wrong or the poll may no longer exist.</div>
          </CardBody>
        </Card>
      );
    }

    if (!canHostExistingPoll) {
      return (
        <Card>
          <CardHeader
            title="Poll host"
            right={
              <Button variant="secondary" onClick={goBack}>
                Back
              </Button>
            }
          />
          <CardBody className="space-y-1">
            <div className="text-sm font-semibold text-zinc-900">Host access unavailable</div>
            <div className="text-sm text-zinc-600">
              Host controls are limited to named session presenters and committee admins.
            </div>
          </CardBody>
        </Card>
      );
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      {step === 'create' ? (
        <Card className="overflow-hidden">
          <div className="flex items-start justify-between gap-3 border-b border-zinc-300 bg-zinc-200 px-4 py-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-zinc-900">Polls</div>
              <div className="mt-1 truncate text-xs text-zinc-600">Create a live poll and share the QR code.</div>
            </div>
          </div>

          <CardBody className="space-y-4">
            <div>
              <div className="mb-2 text-xs font-semibold text-zinc-600">Question</div>
              <Input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="e.g. Which feature should we build next?"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold text-zinc-600">Answers</div>
                <Button variant="ghost" onClick={addAnswer}>
                  + Add
                </Button>
              </div>
              <div className="grid gap-2">
                {answers.map((a, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <Input value={a} onChange={(e) => updateAnswer(i, e.target.value)} placeholder={`Option ${i + 1}`} />
                    </div>
                    {answers.length > 2 ? (
                      <Button variant="ghost" onClick={() => removeAnswer(i)}>
                        Remove
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            {questionsQueue.length ? (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-zinc-600">Queued questions</div>
                <div className="grid gap-2">
                  {questionsQueue.map((q, idx) => (
                    <div key={idx} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-white p-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold">{idx + 1}. {q.question}</div>
                        <div className="mt-1 truncate text-xs text-zinc-600">{q.options.join(' • ')}</div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="ghost" onClick={() => moveQueued(idx, -1)} disabled={idx === 0}>
                          Up
                        </Button>
                        <Button variant="ghost" onClick={() => moveQueued(idx, 1)} disabled={idx === questionsQueue.length - 1}>
                          Down
                        </Button>
                        <Button variant="ghost" onClick={() => removeQueued(idx)}>
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" disabled={!canCreate} onClick={addToQueue}>
                Add to queue
              </Button>
              <Button onClick={onCreate} disabled={creating || (questionsQueue.length === 0 && !canCreate)}>
                {creating ? 'Starting…' : 'Start poll'}
              </Button>
              <Button variant="ghost" onClick={reset}>
                Reset
              </Button>
            </div>

            <div className="text-xs text-zinc-600">
              Add one or more questions to the queue, then start the poll. Use Previous/Next to move between questions.
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className={liveGridClass}>
          <PollResultsCard
            question={activeQuestion?.question || 'Loading…'}
            options={activeQuestion?.options || []}
            height={chartHeight}
            toolbar={
              <>
                {existingHostRoute ? (
                  <Button variant="secondary" onClick={goBack}>
                    Back
                  </Button>
                ) : null}
                <Button variant="ghost" onClick={goPrev} disabled={!pollId || currentIdx <= 0}>
                  Previous
                </Button>
                <Button variant="ghost" onClick={goNext} disabled={!pollId || currentIdx >= totalQuestions - 1}>
                  Next
                </Button>
                {totalQuestions ? (
                  <div className="text-xs text-zinc-600" style={{ whiteSpace: 'nowrap', padding: '0 4px' }}>
                    Q {currentIdx + 1}/{totalQuestions}
                  </div>
                ) : null}
                <Button variant="ghost" onClick={() => shareUrl && navigator.clipboard?.writeText(shareUrl)} disabled={!shareUrl}>
                  Copy link
                </Button>
                <Button variant="ghost" onClick={() => setShowQr((v) => !v)} disabled={!shareUrl} aria-pressed={showQr}>
                  QR code
                </Button>
                <Button variant="ghost" onClick={resetVotes} disabled={!pollId}>
                  Clear this Q's votes
                </Button>
                {!existingHostRoute ? (
                  <Button variant="ghost" onClick={reset}>
                    New poll
                  </Button>
                ) : null}
              </>
            }
          />

          {showQrPanel ? (
            <div className="flex h-full items-center justify-center overflow-hidden rounded-2xl border border-zinc-200 bg-white p-3">
              <QRCodeCanvas
                value={shareUrl!}
                size={qrSize}
                includeMargin={false}
                style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export function PollRespondPage() {
  useBodyClass('poll-respond');
  const { pollId } = useParams();
  const { poll, loading, error } = usePoll(pollId ?? null);
  const [choiceIndexes, setChoiceIndexes] = React.useState<number[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { h: vh } = useWindowSize();

  const voterId = React.useMemo(() => getVoterId(), []);

  const currentIdx = typeof poll?.currentIdx === 'number' ? poll.currentIdx : 0;
  const totalQuestions = Array.isArray(poll?.questions) ? poll.questions.length : 0;
  const activeQuestion = totalQuestions ? poll.questions[currentIdx] : null;
  const resetCounter = activeQuestion?.resetCounter || 0;
  const activeQuestionType = questionType(activeQuestion);
  const maxChoices = activeQuestion ? questionMaxChoices(activeQuestion) : 1;
  const availableFrom = pollAvailableFromMs(poll);
  const availableUntil = pollAvailableUntilMs(poll);
  const pollUnavailable = availableFrom != null && availableFrom > nowMs;
  const pollClosed = availableUntil != null && availableUntil <= nowMs;

  const resultsHeight = React.useMemo(() => {
    if (!isMobile) return 420;
    return Math.max(240, Math.min(320, vh - 280));
  }, [isMobile, vh]);

  React.useEffect(() => {
    const availableAt = pollAvailableFromMs(poll);
    const availableUntil = pollAvailableUntilMs(poll);
    const futureTimes = [availableAt, availableUntil]
      .filter((value): value is number => typeof value === 'number' && value > nowMs)
      .sort((a, b) => a - b);

    const next = futureTimes[0];
    if (!next) return;

    const timeout = window.setTimeout(
      () => setNowMs(Date.now()),
      Math.min(next - nowMs, 2_147_483_647)
    );
    return () => window.clearTimeout(timeout);
  }, [poll, nowMs]);

  React.useEffect(() => {
    if (!pollId || !poll || !activeQuestion) return;

    const local = readLocalVote(pollId, currentIdx);
    const choices = choiceIndexesFromStoredVote(local, activeQuestion);
    const isMultiChoice = questionType(activeQuestion) === 'multi-choice';
    const hasCurrentMultiChoiceVote = !isMultiChoice || Array.isArray(local?.choiceIdxs);
    const valid = local && local.resetCounter === resetCounter && choices.length > 0 && hasCurrentMultiChoiceVote;

    if (valid) {
      setChoiceIndexes(choices);
      setDone(true);
    } else {
      setChoiceIndexes(isMultiChoice && choices.length ? choices : []);
      setDone(false);
      if (local && local.resetCounter !== resetCounter) {
        clearLocalVote(pollId, currentIdx);
      }
    }
    setSubmitting(false);
  }, [pollId, poll, currentIdx, resetCounter, activeQuestion]);

  function toggleChoice(idx: number) {
    if (done || submitting) return;
    setChoiceIndexes((prev) => {
      if (prev.includes(idx)) return prev.filter((value) => value !== idx);
      if (activeQuestionType !== 'multi-choice') return [idx];
      if (prev.length >= maxChoices) return prev;
      return [...prev, idx].sort((a, b) => a - b);
    });
  }

  async function submitVote(choices: number[]) {
    if (!pollId || done || pollUnavailable || pollClosed || !activeQuestion || !choices.length) return;
    setSubmitting(true);
    try {
      await vote(pollId, currentIdx, choices, voterId);
      writeLocalVote(pollId, currentIdx, storedVotePayload(activeQuestion, choices, resetCounter));
      setChoiceIndexes(choices);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  }

  function onOptionClick(idx: number) {
    if (activeQuestionType === 'multi-choice') {
      toggleChoice(idx);
      return;
    }
    setChoiceIndexes([idx]);
    void submitVote([idx]);
  }

  if (loading) {
    return (
      <div className="center-screen">
        <div className="small">Loading poll…</div>
      </div>
    );
  }

  if (error || !poll) {
    return (
      <div className="center-screen" style={{ padding: 24 }}>
        <LegacyCard className="p-24" style={{ maxWidth: 520, width: '100%' }}>
          <div style={{ fontSize: 18, fontWeight: 750 }}>Poll not found</div>
          <div className="small mt-12">The link may be wrong or the poll was removed.</div>
        </LegacyCard>
      </div>
    );
  }

  if (!activeQuestion) {
    return (
      <div className="center-screen" style={{ padding: 24 }}>
        <LegacyCard className="p-24" style={{ maxWidth: 520, width: '100%' }}>
          <div style={{ fontSize: 18, fontWeight: 750 }}>Poll not ready</div>
          <div className="small mt-12">The host hasn’t started the poll yet.</div>
        </LegacyCard>
      </div>
    );
  }

  if (pollUnavailable && availableFrom != null) {
    return (
      <div className="center-screen" style={{ padding: 24 }}>
        <LegacyCard className="p-24" style={{ maxWidth: 560, width: '100%' }}>
          <div style={{ fontSize: 18, fontWeight: 750 }}>Poll not open yet</div>
          <div className="small mt-12">This poll is available from {formatPollAvailableFrom(availableFrom)}.</div>
        </LegacyCard>
      </div>
    );
  }

  if (pollClosed && availableUntil != null) {
    return (
      <div className="center-screen" style={{ padding: 24 }}>
        <LegacyCard className="p-24" style={{ maxWidth: 560, width: '100%' }}>
          <div style={{ fontSize: 18, fontWeight: 750 }}>Poll closed</div>
          <div className="small mt-12">This poll closed at {formatPollAvailableFrom(availableUntil)}.</div>
          <div className="poll-hub-link-wrap">
            <a href="/conference" className="poll-hub-link">
              Go back to Hub
            </a>
          </div>
        </LegacyCard>
      </div>
    );
  }

  const instruction =
    activeQuestionType === 'multi-choice'
      ? `Choose up to ${maxChoices} option${maxChoices === 1 ? '' : 's'}, then submit.`
      : 'Tap an option to submit your vote. Then you’ll see results.';
  const canSubmitMultiChoice =
    activeQuestionType === 'multi-choice' && choiceIndexes.length > 0 && choiceIndexes.length <= maxChoices && !submitting;

  return (
    <div>
      <div className="container" style={{ maxWidth: 920, paddingTop: 24 }}>
        {!done ? (
          <div className="mb-16">
            {totalQuestions ? (
              <div className="small" style={{ opacity: 0.8, marginBottom: 6 }}>
                Question {currentIdx + 1} of {totalQuestions}
              </div>
            ) : null}
            <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{activeQuestion.question}</div>
          </div>
        ) : null}

        {!done ? (
          <LegacyCard className="p-16">
            <div style={{ display: 'grid', gap: 12 }}>
              {(activeQuestion.options || []).map((o: any, idx: number) => (
                <button
                  key={idx}
                  onClick={() => onOptionClick(idx)}
                  disabled={submitting}
                  className={'option-btn' + (choiceIndexes.includes(idx) ? ' selected' : '')}
                >
                  <div style={{ fontWeight: 700 }}>{o.label}</div>
                </button>
              ))}
            </div>
            {activeQuestionType === 'multi-choice' ? (
              <div className="hstack between gap-12 mt-16" style={{ alignItems: 'center' }}>
                <div className="small">
                  {choiceIndexes.length}/{maxChoices} selected
                </div>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!canSubmitMultiChoice}
                  onClick={() => void submitVote(choiceIndexes)}
                >
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            ) : null}
            <div className="small mt-12">{instruction}</div>
          </LegacyCard>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            {isMobile ? (
              <LegacyResultsList question={activeQuestion.question} options={activeQuestion.options || []} />
            ) : (
              <LegacyPieResults question={activeQuestion.question} options={activeQuestion.options || []} height={resultsHeight} />
            )}

            <div className="small" style={{ textAlign: 'center' }}>
              Results update live as others vote.
            </div>
          </div>
        )}
        <div className="poll-hub-link-wrap">
          <a href="/conference" className="poll-hub-link">
            Go back to Hub
          </a>
        </div>
      </div>
    </div>
  );
}
